import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";
import {
  hpaAssessmentProgress,
  hpaCreateAssessment,
  hpaFinalizeAssessment,
  hpaNextQuestion,
  hpaPauseAssessment,
  hpaResumeAssessment,
  hpaStartAssessment,
  hpaSubmitAnswer,
} from "../../lib/hpaApi";
import {
  HpaErrorState,
  HpaJourneyShell,
  HpaQuestionCard,
  HpaTopBar,
} from "./components";
import "./hpa.css";
import "./hpa-assessment.css";

/**
 * SAT HPA™ · Block 11.2 · Assessment Screen
 *
 * Flow (§ 4):
 *   - existiert kein aktives Assessment → CREATE + START
 *   - existiert eins → RESUME (falls PAUSED) und weiterlaufen
 *   - Next-Question laden; wenn null → Completion-Bridge zeigen
 *
 * State-Machine (frontend-internal, backend bleibt kanonisch):
 *   loading → question | completion | paused | error
 *
 * Backend ist Source of Truth. Kein localStorage für Antworten (§ 41).
 * Assessment-ID wird nur in sessionStorage (nicht localStorage) gehalten.
 */

const SS_ASSESSMENT_KEY = "hpa_active_assessment_id";

function loadActiveAssessmentId() {
  try {
    return window.sessionStorage.getItem(SS_ASSESSMENT_KEY);
  } catch (_e) {
    return null;
  }
}
function saveActiveAssessmentId(id) {
  try {
    window.sessionStorage.setItem(SS_ASSESSMENT_KEY, id);
  } catch (_e) {
    /* noop */
  }
}
function clearActiveAssessmentId() {
  try {
    window.sessionStorage.removeItem(SS_ASSESSMENT_KEY);
  } catch (_e) {
    /* noop */
  }
}

export default function HpaAssessment() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading|question|completion|error
  const [assessmentId, setAssessmentId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selectingOptionId, setSelectingOptionId] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [errorMsg, setErrorMsg] = useState(null);
  const [progress, setProgress] = useState({ phase: null, completedItems: 0 });
  const [finalizing, setFinalizing] = useState(false);

  // Prevent double submit / double init (§ 13)
  const busyRef = useRef(false);

  // ---------------------------------------------------------------- Bootstrap
  const bootstrap = useCallback(async () => {
    setStatus("loading");
    setErrorMsg(null);

    // 1. Aktives Assessment aus sessionStorage laden — wenn vorhanden, versuche direkt Next-Question
    const existingId = loadActiveAssessmentId();

    const tryFetchQuestion = async (id) => {
      const q = await hpaNextQuestion(id);
      if (q === null || q === undefined || q === "") {
        setStatus("completion");
        setQuestion(null);
        return;
      }
      setQuestion(q);
      setProgress((p) => ({
        phase: q.phase,
        completedItems: typeof q.completed_items === "number"
          ? q.completed_items
          : p.completedItems,
      }));
      setStatus("question");
    };

    try {
      if (existingId) {
        setAssessmentId(existingId);
        try {
          await tryFetchQuestion(existingId);
          return;
        } catch (err) {
          const code = err?.response?.status;
          if (code === 409) {
            // Wahrscheinlich PAUSED → resume und erneut versuchen
            try {
              await hpaResumeAssessment(existingId);
              await tryFetchQuestion(existingId);
              return;
            } catch (_inner) {
              /* fällt in create-Zweig */
            }
          }
          if (code === 404) {
            clearActiveAssessmentId();
          } else {
            throw err;
          }
        }
      }
      // 2. Neues Assessment anlegen + starten
      const created = await hpaCreateAssessment({});
      const id = created.assessment_id;
      saveActiveAssessmentId(id);
      setAssessmentId(id);
      await hpaStartAssessment(id);
      await tryFetchQuestion(id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        detail?.error_code === "CONSENT_REQUIRED"
          ? "Bevor du beginnst, brauchen wir noch dein Einverständnis."
          : detail?.error_code === "HPA_DISABLED"
          ? "Die HPA-Preview ist derzeit nicht verfügbar."
          : "Die HPA konnte gerade nicht geladen werden.";
      setErrorMsg(msg);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // ---------------------------------------------------------------- Answering
  const answer = useCallback(
    async (option) => {
      if (busyRef.current || !assessmentId || !question) return;
      busyRef.current = true;
      setSelectingOptionId(option.option_id);
      setSaveState("saving");
      try {
        await hpaSubmitAnswer(assessmentId, {
          item_id: question.item_id,
          option_id: option.option_id,
          // Block 11.2a · Scoring-Leak-Fix: KEIN `score` mitsenden.
          // Backend resolvt kanonisch aus gepinnter Question-Bank.
        });
        setSaveState("saved");
        // Nächste Frage holen (backend bleibt kanonisch)
        const nextQ = await hpaNextQuestion(assessmentId);
        if (nextQ === null || nextQ === undefined || nextQ === "") {
          setStatus("completion");
          setQuestion(null);
        } else {
          setQuestion(nextQ);
          setProgress({
            phase: nextQ.phase,
            completedItems: typeof nextQ.completed_items === "number"
              ? nextQ.completed_items
              : progress.completedItems + 1,
          });
        }
        setSelectingOptionId(null);
        // Save-State kurz sichtbar lassen, dann verstecken
        setTimeout(() => setSaveState("idle"), 900);
      } catch (err) {
        // § 15 · Antwort behalten, klare Fehlermeldung
        setSaveState("error");
        setSelectingOptionId(null);
        const code = err?.response?.status;
        if (code === 409) {
          setErrorMsg(
            "Deine HPA wurde an anderer Stelle aktualisiert. Bitte lade den aktuellen Stand."
          );
          setStatus("error");
        } else if (code === 429) {
          setErrorMsg(
            "Bitte einen kleinen Moment — dein Rhythmus ist gerade zu schnell."
          );
        } else {
          setErrorMsg(
            "Deine Antwort konnte gerade nicht gespeichert werden. Bitte versuche es noch einmal. Deine Reise bleibt an dieser Stelle."
          );
        }
      } finally {
        busyRef.current = false;
      }
    },
    [assessmentId, question, progress.completedItems]
  );

  // ---------------------------------------------------------------- Pause
  const doPause = useCallback(async () => {
    if (!assessmentId) return;
    try {
      await hpaPauseAssessment(assessmentId);
      navigate("/hpa/paused");
    } catch (_err) {
      setErrorMsg("Pause konnte gerade nicht gespeichert werden.");
    }
  }, [assessmentId, navigate]);

  // ---------------------------------------------------------------- Finalize
  const doFinalize = useCallback(async () => {
    if (!assessmentId || finalizing) return;
    setFinalizing(true);
    setErrorMsg(null);
    try {
      const resp = await hpaFinalizeAssessment(assessmentId);
      // Aktives Assessment abgeschlossen — session marker entfernen,
      // damit /hpa nicht in einen Zombie-Zustand fällt
      clearActiveAssessmentId();
      if (resp.mentor_review_required) {
        navigate("/hpa/review-pending", {
          state: { profileId: resp.profile_id },
        });
      } else {
        navigate("/hpa"); // Block 11.3 → Profile Reveal
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error_code === "STATE_CONFLICT") {
        setErrorMsg(
          "Dein Assessment ist noch nicht vollständig. Bitte beantworte noch ein paar Fragen."
        );
        // Zurück zur Question-Phase
        bootstrap();
      } else {
        setErrorMsg("Dein Profil konnte gerade nicht erstellt werden.");
      }
      setFinalizing(false);
    }
  }, [assessmentId, finalizing, navigate, bootstrap]);

  // ---------------------------------------------------------------- Render
  return (
    <>
      <SEO title="HPA · Assessment" description="HPA Assessment" path="/hpa/assessment" />
      <HpaJourneyShell testid="hpa-assessment">
        {status === "loading" && (
          <div
            className="text-[color:var(--hpa-text-mute)] mt-16 text-sm tracking-widest uppercase"
            data-testid="hpa-assessment-loading"
          >
            Deine HPA wird geöffnet…
          </div>
        )}

        {status === "error" && (
          <div className="mt-8" data-testid="hpa-assessment-error">
            <HpaErrorState
              message={errorMsg || "Kurze Unterbrechung."}
              onRetry={bootstrap}
            />
          </div>
        )}

        {status === "question" && question && (
          <>
            <HpaTopBar
              phase={progress.phase}
              completedItems={progress.completedItems}
              onPause={doPause}
              pauseDisabled={saveState === "saving"}
            />
            <HpaQuestionCard
              question={question}
              onAnswer={answer}
              selectingOptionId={selectingOptionId}
              disabled={saveState === "saving"}
              saveState={saveState}
            />
            {errorMsg && (
              <div className="mt-8">
                <HpaErrorState
                  message={errorMsg}
                  onRetry={() => {
                    setErrorMsg(null);
                    setSaveState("idle");
                  }}
                />
              </div>
            )}
          </>
        )}

        {status === "completion" && (
          <div data-testid="hpa-completion">
            <div className="hpa-overline mb-6">SAT HPA™ · Abschluss</div>
            <h1 className="hpa-headline text-3xl md:text-5xl mb-8">
              Du hast den ersten Teil <em>abgeschlossen</em>.
            </h1>
            <p className="hpa-lede text-base md:text-lg leading-relaxed max-w-xl mb-14">
              Deine Antworten sind vollständig. Jetzt kann daraus dein
              persönliches HPA-Profil entstehen.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <button
                type="button"
                className="hpa-btn-primary"
                disabled={finalizing}
                onClick={doFinalize}
                data-testid="hpa-completion-cta-finalize"
              >
                {finalizing ? "Wird erstellt…" : "Mein Profil erstellen"}
              </button>
              <button
                type="button"
                className="hpa-btn-ghost"
                onClick={doPause}
                data-testid="hpa-completion-cta-pause"
              >
                Später abschließen
              </button>
            </div>
            {errorMsg && (
              <div className="mt-8">
                <HpaErrorState message={errorMsg} onRetry={() => setErrorMsg(null)} />
              </div>
            )}
          </div>
        )}
      </HpaJourneyShell>
    </>
  );
}
